import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SaveAltOutlinedIcon from '@mui/icons-material/SaveAltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import { useOnboardingState } from '../../hooks/useOnboardingState.jsx';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import VideoCardV2 from '@/component/VideoCardV2';
import Logo from '@/Asset/images/logo-blaxk.webp';
import Toast from '@/utils/toastManager';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import EastIcon from '@mui/icons-material/East';
import './RecommendedPlaylist.css';

const RecommendedVideos = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { state, clearState } = useOnboardingState();
	const planFromQuery = new URLSearchParams(location.search).get('plan');
	const roleFromQuery = new URLSearchParams(location.search).get('role');
	const isRetake = new URLSearchParams(location.search).get('retake') === 'true' || location.state?.isRetake === true;
	const isStudioPlan = state.selectedPlanType === 'studio' || state.userType === 'studio-owner' || planFromQuery === 'studio';
	const selectedRole = roleFromQuery || state.onboardingRole || null;
	const [saveMode, setSaveMode] = useState('studio');
	const [recommendedVideos, setRecommendedVideos] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [savedPlaylistId, setSavedPlaylistId] = useState(null);
	const [showFeedback, setShowFeedback] = useState(false);
	const [feedbackGiven, setFeedbackGiven] = useState(null); // 'yes' | 'no' | null
	const pendingAction = useRef(null); // stores the fn to run after feedback

	// Run pending action and close popup
	const completeFeedback = (answer) => {
		setFeedbackGiven(answer);
		setShowFeedback(false);
		onboardingV2Api.saveRecommendationFeedback(answer).catch(err =>
			console.error('[Feedback] Failed to save:', err)
		);
		if (pendingAction.current) {
			pendingAction.current();
			pendingAction.current = null;
		}
	};

	// Close without answering — still run the action
	const dismissFeedback = () => {
		setShowFeedback(false);
		if (pendingAction.current) {
			pendingAction.current();
			pendingAction.current = null;
		}
	};

	// Wrap any action: show popup first, execute action after
	const withFeedback = (action) => {
		pendingAction.current = action;
		setFeedbackGiven(null);
		setShowFeedback(true);
	};

	useEffect(() => {
		const fetchRecommendedVideos = async () => {
			try {
				const response = await onboardingV2Api.getRecommendedVideos();
				if (response.data?.videos) {
					setRecommendedVideos(response.data.videos);
				}
			} catch (error) {
				console.error('Error fetching recommended videos:', error);
				Toast.error('Failed to load recommended videos');
			} finally {
				setIsLoading(false);
			}
		};
		fetchRecommendedVideos();
	}, []);

	const handleVideoClick = videoId => {
		withFeedback(() => window.open(`/PlayerPage?v=${videoId}&type=classes`, '_blank'));
	};

	const handleFinish = (selectedMode = saveMode) => {
		withFeedback(() => _doFinish(selectedMode));
	};

	const _doFinish = async (selectedMode = saveMode) => {
		try {
			const videoIds = recommendedVideos.map(video => video._id);
			const playlistType = (isStudioPlan && selectedMode === 'studio') ? 'studio' : 'personal';
			const result = await onboardingV2Api.saveRecommendedAsPlaylist({ videoIds, playlistType });
			await onboardingV2Api.completeOnboarding();
			window?.fbq?.('trackCustom', 'OnboardingV2_Completed', { CustomEvent: 'OnboardingV2_Completed' });
			setSavedPlaylistId(result.data?.playlistId || result.data?.playlist?._id);
			Toast.success('Playlist Saved');
			clearState();
		} catch (error) {
			console.error('Failed to complete onboarding:', error);
			Toast.error('Failed to save. Redirecting to dashboard...');
			clearState();
			navigate('/');
		}
	};

	const handleViewPlaylist = () => {
		if (savedPlaylistId) {
			navigate(`/all-playlist?id=${savedPlaylistId}`);
		}
	};

	const handleSkip = () => {
		withFeedback(() => _doSkip());
	};

	const _doSkip = async () => {
		try {
			await onboardingV2Api.completeOnboarding();
			window?.fbq?.('trackCustom', 'OnboardingV2_Completed', { CustomEvent: 'OnboardingV2_Completed', skipped: true });
		} catch (error) {
			console.error('Failed to mark onboarding complete:', error);
		}
		clearState();
		navigate('/');
	};

	return (
		<div className="onboarding-reco">
			<div className="onboarding-reco__grid-overlay" />
			<div className="onboarding-reco__content">
				<header className="onboarding-reco__header">
					<div className="onboarding-reco__brand-row">
						<button
							type="button"
							className="onboarding-reco__back-btn"
							onClick={() => {
								const params = new URLSearchParams(location.search).toString();
								const path = isStudioPlan ? (isRetake ? '/onboarding/step-5' : '/onboarding/step-6') : (isRetake && selectedRole === 'dancer-parent' ? '/onboarding/step-3' : '/onboarding/step-4');
								navigate(`${path}?${params}`);
							}}
						>
							<KeyboardBackspaceOutlinedIcon />
						</button>
						<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-reco__logo" />
					</div>
				</header>

				{isStudioPlan ? (
					<main className="onboarding-reco__main">
						<h1 className="onboarding-reco__title">STUDIO RECOMMENDATIONS</h1>
						<p className="onboarding-reco__subtitle">Based on your goals, start with these series:</p>

						{isLoading ? (
							<div style={{ textAlign: 'center', padding: '50px', color: 'white' }}>Loading videos...</div>
						) : recommendedVideos.length === 0 ? (
							<div style={{ textAlign: 'center', padding: '50px', color: 'white' }}>No videos available</div>
						) : (
							<div className="recommended-playlist">
								{recommendedVideos.map(video => (
									<div key={video._id}>
										<div onClick={() => handleVideoClick(video._id)} style={{ cursor: 'pointer', position: 'relative' }}>
											<VideoCardV2
												video={video}
												customStyles={{ minWidth: '400px' }}
												hoverEffect={true}
											/>
										</div>
									</div>
								))}
							</div>
						)}

						<div className="onboarding-reco__actions onboarding-reco__actions--studio">
							<div className="onboarding-reco__save-wrap">
								<div className="onboarding-reco__tooltip-wrap onboarding-reco__tooltip-wrap--save">
									<button
										type="button"
										className="onboarding-reco__secondary-btn onboarding-reco__secondary-btn--studio"
									>
										<InfoOutlinedIcon/>
										Save As Playlist
										<SubscriptionsIcon style={{ marginLeft: '8px', fontSize: '18px' }} />
									</button>
									<div className="onboarding-reco__tooltip">
										<p>
											<strong>Personal Playlist:</strong>
										</p>
										<p>Private playlists created for your own training or class planning are visible only within your individual account.</p>
										<p>
											<strong>Studio Playlist:</strong>
										</p>
										<p>Shared playlists allow all assigned instructors to access the playlist and stay aligned in training and class structure.</p>
									</div>
								</div>
							</div>

							{savedPlaylistId ? (
								<button
									type="button"
									className="onboarding-reco__secondary-btn onboarding-reco__secondary-btn--studio"
									onClick={handleViewPlaylist}
								>
									View Playlist
									<SubscriptionsIcon style={{ marginLeft: '8px', fontSize: '18px' }} />
								</button>
							) : (
								<div className="onboarding-reco__mode-toggle">
									<button
										type="button"
										className={`onboarding-reco__mode-btn ${saveMode === 'studio' ? 'is-active' : ''}`}
										onClick={() => {
											setSaveMode('studio');
											handleFinish('studio');
										}}
									>
										<StorefrontOutlinedIcon />
										Studio
									</button>
									<button
										type="button"
										className={`onboarding-reco__mode-btn ${saveMode === 'personal' ? 'is-active' : ''}`}
										onClick={() => {
											setSaveMode('personal');
											handleFinish('personal');
										}}
									>
										<PersonOutlineOutlinedIcon />
										Personal
									</button>
								</div>
							)}

							<button type="button" onClick={handleSkip} className="onboarding-reco__primary-btn">
								Start Training <EastIcon/>
							</button>
						</div>
					</main>
				) : (
					<main className="onboarding-reco__main">
						<h1 className="onboarding-reco__title">Recommended For You</h1>
						<p className="onboarding-reco__subtitle">Based on your goals, start with these series:</p>

						{isLoading ? (
							<div style={{ textAlign: 'center', padding: '50px', color: 'white' }}>Loading videos...</div>
						) : recommendedVideos.length === 0 ? (
							<div style={{ textAlign: 'center', padding: '50px', color: 'white' }}>No videos available</div>
						) : (
							<div className="recommended-playlist">
								{recommendedVideos.map(video => (
									<div key={video._id}>
										<div onClick={() => handleVideoClick(video._id)} style={{ cursor: 'pointer', position: 'relative' }}>
											<VideoCardV2
												video={video}
												customStyles={{ minWidth: '400px' }}
												hoverEffect={true}
											/>
										</div>
									</div>
								))}
							</div>
						)}

						<div className="onboarding-reco__actions">
							<div className="onboarding-reco__tooltip-wrap">
								<button 
									type="button" 
									onClick={savedPlaylistId ? handleViewPlaylist : handleFinish} 
									className="onboarding-reco__secondary-btn"
								>
									{savedPlaylistId ? 'View Playlist' : 'Save As Playlist'}
									<SubscriptionsIcon style={{ marginLeft: '8px', fontSize: '18px' }} />
								</button>
							</div>
							<button type="button" onClick={handleSkip} className="onboarding-reco__primary-btn">
								Start Training <EastIcon/>
							</button>
						</div>
					</main>
				)}
			</div>

			{/* ── Feedback Popup ── */}
			{showFeedback && (
				<div className="onboarding-reco__feedback-overlay" onClick={dismissFeedback}>
					<div className="onboarding-reco__feedback-modal" onClick={e => e.stopPropagation()}>
						<button
							type="button"
							className="onboarding-reco__feedback-close"
							onClick={dismissFeedback}
							aria-label="Close"
						>
							<CloseIcon fontSize="small" />
						</button>
						<p className="onboarding-reco__feedback-question">Do you feel these suggestions are helpful?</p>
						<div className="onboarding-reco__feedback-btns">
							<button
								type="button"
								className="onboarding-reco__feedback-btn onboarding-reco__feedback-btn--yes"
								onClick={() => completeFeedback('yes')}
							>
								<ThumbUpOutlinedIcon fontSize="small" /> Yes
							</button>
							<button
								type="button"
								className="onboarding-reco__feedback-btn onboarding-reco__feedback-btn--no"
								onClick={() => completeFeedback('no')}
							>
								<ThumbDownOutlinedIcon fontSize="small" /> No
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default RecommendedVideos;
